// Pure AR calculation functions — no Firestore/React imports.
import { round } from "./whtCalculator";

export const AGING_BUCKETS = Object.freeze({
  CURRENT: "CURRENT",
  DAYS_1_30: "1-30",
  DAYS_31_60: "31-60",
  DAYS_61_90: "61-90",
  DAYS_90_PLUS: "90+"
});

// Outstanding receivable = gross invoice - allocated cash - recognized WHT -
// recognized GST withheld - other recognized deductions - approved credit
// adjustments. Matches requirement 10's formula exactly (Model 1/Model 2's
// expected-WHT split already happened upstream in whtCalculator.js — this
// function only nets ACTUAL/recognized amounts, never re-derives revenue).
export function calculateOutstanding({
  grossAmount, allocatedAmount = 0, whtRecognized = 0, gstWithheldRecognized = 0,
  otherDeductionsRecognized = 0, creditAdjustments = 0
}) {
  return round(
    Number(grossAmount || 0)
    - Number(allocatedAmount || 0)
    - Number(whtRecognized || 0)
    - Number(gstWithheldRecognized || 0)
    - Number(otherDeductionsRecognized || 0)
    - Number(creditAdjustments || 0)
  );
}

export function daysBetween(fromDateStr, toDateStr) {
  const from = new Date(`${fromDateStr}T00:00:00Z`).getTime();
  const to = new Date(`${toDateStr}T00:00:00Z`).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.floor((to - from) / (24 * 60 * 60 * 1000));
}

export function addDays(dateStr, days) {
  const date = new Date(`${dateStr}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

// Ages off the invoice date (or an explicit due date, if the caller has one)
// as of `today`. Negative/zero days -> CURRENT.
export function agingBucket(daysOutstanding) {
  if (daysOutstanding <= 0) return AGING_BUCKETS.CURRENT;
  if (daysOutstanding <= 30) return AGING_BUCKETS.DAYS_1_30;
  if (daysOutstanding <= 60) return AGING_BUCKETS.DAYS_31_60;
  if (daysOutstanding <= 90) return AGING_BUCKETS.DAYS_61_90;
  return AGING_BUCKETS.DAYS_90_PLUS;
}

export const RECEIVABLE_STATUSES = Object.freeze({
  UNPAID: "UNPAID",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  RECONCILED: "RECONCILED",
  EXCESS_RECEIVED: "EXCESS_RECEIVED",
  OVERDUE: "OVERDUE"
});

// Automatically derivable statuses only (5 of the spec's 7 — "Short received"
// and "Adjustment required" are left as a manual classification finance
// applies via a journal adjustment/note, since distinguishing "short received
// and no more is coming" from "partially paid, more expected" is a judgment
// call, not something derivable from the numbers alone). Documented scoping
// decision for Stage 2, not a silent omission.
//
// OVERDUE takes priority over UNPAID/PARTIALLY_PAID whenever money is still
// owed past the due date — it describes time, not amount, so it can apply to
// either an unpaid or a partially-paid invoice.
//
// `received` is everything already applied to the invoice (cash, recognised
// WHT/GST withheld, other deductions, adjustments). An invoice with a balance
// and nothing applied yet is UNPAID, not PARTIALLY_PAID.
export function receivableStatus({ outstanding, isPastDue, tolerance = 5, received = 0 }) {
  if (Math.abs(outstanding) <= tolerance) return RECEIVABLE_STATUSES.RECONCILED;
  if (outstanding < 0) return RECEIVABLE_STATUSES.EXCESS_RECEIVED;
  if (isPastDue) return RECEIVABLE_STATUSES.OVERDUE;
  return Math.abs(Number(received || 0)) > tolerance ? RECEIVABLE_STATUSES.PARTIALLY_PAID : RECEIVABLE_STATUSES.UNPAID;
}

// Splits a received amount proportionally across selected invoices by their
// expected receipt, giving the rounding remainder to the last row so the
// allocations always sum exactly to the received amount — the same technique
// already used by CortevaReconciliation.js's saveReceipt().
export function allocateProportionally(receivedAmount, selections) {
  const totalExpected = selections.reduce((sum, s) => sum + Number(s.expectedReceipt || 0), 0);
  if (!(totalExpected > 0)) throw new Error("The selected invoices do not have a valid expected receipt to allocate against.");
  let allocated = 0;
  return selections.map((selection, index) => {
    const amount = index === selections.length - 1
      ? round(receivedAmount) - allocated
      : round(round(receivedAmount) * Number(selection.expectedReceipt || 0) / totalExpected);
    allocated += amount;
    return { ...selection, allocatedAmount: amount };
  });
}
